/**
 * Billing API
 *
 * RTK Query endpoints for BeHeard's billing READ (GET /billing/status) and the
 * hand-offs to Central billing (portal / checkout). BeHeard sells nothing:
 * there are no credit products, bundles or wallets here — credits,
 * prices and settlement are Central's (docs/PLAN-AUTHORITY.md).
 *
 * GET /billing/status speaks ONE contract, `@beheard/billing-contract` — the
 * very declaration the server answers with (backend/src/contracts/billingStatus.ts).
 */

import { api } from '../api';
import {
  SubscriptionStatus,
  normalizeSubscriptionStatus,
  isActiveSubscription,
} from '@levelup/central-billing';
import type { BillingStatus } from '@beheard/billing-contract';

// Re-export shared billing contracts for consumers that import from billingApi
export type { SubscriptionStatus, BillingStatus };
export type {
  AllowancePoolKey,
  BillingPlanTier,
  CreditPoolStatus,
  SafetyAllowanceStatus,
  SubscriptionView,
} from '@beheard/billing-contract';
export {
  BILLING_STATUS_UNAVAILABLE_CODE,
  BILLING_STATUS_UNAVAILABLE_MESSAGE,
  billingStatusViolations,
} from '@beheard/billing-contract';
export { normalizeSubscriptionStatus, isActiveSubscription };

export type PlanTier = 'free' | 'pro' | 'pro_ai';
export type BillingInterval = 'monthly' | 'yearly';

export interface CapabilityInfo {
  key: string;
  label: string;
  description: string;
}

export interface CreateCheckoutSessionRequest {
  mode: 'subscription';
  planTier?: PlanTier;
  planKey?: string;
  billingInterval?: BillingInterval;
}

export interface CreateCheckoutSessionResponse {
  checkoutUrl: string;
}

export interface CreatePortalSessionResponse {
  portalUrl: string;
}

export interface PlanDto {
  key: string;
  name: string;
  description: string;
  capabilities: CapabilityInfo[];
}

export interface AddOnDto {
  key: string;
  name: string;
  description: string;
  requiredPlanKeys: string[];
  capabilities: CapabilityInfo[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const billingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Hand-off to Central billing for a subscription change.
     */
    createCheckoutSession: builder.mutation<CreateCheckoutSessionResponse, CreateCheckoutSessionRequest>({
      query: (body) => ({
        url: '/billing/create-checkout-session',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CreateCheckoutSessionResponse>) => response.data,
    }),

    /**
     * Central billing portal for managing the subscription.
     */
    createPortalSession: builder.mutation<CreatePortalSessionResponse, void>({
      query: () => ({
        url: '/billing/create-portal-session',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CreatePortalSessionResponse>) => response.data,
    }),

    /**
     * The billing-status contract. A 503 BILLING_STATUS_UNAVAILABLE surfaces as
     * the query's error — render "Billing status temporarily unavailable",
     * never a default plan.
     */
    getBillingStatus: builder.query<BillingStatus, void>({
      query: () => '/billing/status',
      transformResponse: (response: ApiResponse<BillingStatus>) => response.data,
      providesTags: ['User'],
    }),

    /**
     * Get available subscription plans (public endpoint).
     */
    getPlans: builder.query<PlanDto[], void>({
      query: () => '/billing/plans',
      transformResponse: (response: ApiResponse<{ plans: PlanDto[] }>) => response.data.plans,
    }),

    /**
     * Get available add-on products (public endpoint).
     */
    getAddOns: builder.query<AddOnDto[], void>({
      query: () => '/billing/addons',
      transformResponse: (response: ApiResponse<{ addOns: AddOnDto[] }>) => response.data.addOns,
    }),
  }),
  overrideExisting: false,
});

// NOTE: getCredits / getCreditProducts / getWallet and the credit checkout
// were removed — BeHeard has no credit products, packs or wallet (those routes
// never existed on the backend). The single billing read is
// useGetBillingStatusQuery.

export const {
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
  useGetBillingStatusQuery,
  useGetPlansQuery,
  useGetAddOnsQuery,
} = billingApi;
